import { useMemo } from "react"

import { membershipApplicationSteps } from "../constants"
import type { MembershipApplicationStep, MembershipApplicationStepId } from "../types"
import {
  useMembershipCategoryConfigurations,
  type MembershipCategoryConfiguration,
} from "@/utils/membership"
import type { MembershipCategorySectionConfig } from "@/utils/membership/types"

/** Steps that should only appear once a category has been chosen. */
const CATEGORY_DEPENDENT_STEPS: Set<MembershipApplicationStepId> = new Set(["requirements", "documents"])

function filterVisibleSteps(configuration: MembershipCategoryConfiguration, categoryId?: number | null) {
  return membershipApplicationSteps.filter((step) => {
    // Hide category-dependent steps until a category is selected
    if (!categoryId && CATEGORY_DEPENDENT_STEPS.has(step.id)) {
      return false
    }
    const section = configuration.sections[step.id]
    return section?.isVisible !== false
  })
}

const OPTIONAL_STEPS: Set<MembershipApplicationStepId> = new Set(["affiliations", "employment", "education"])

function isSectionRequired(
  stepId: MembershipApplicationStepId,
  section: MembershipCategorySectionConfig | undefined,
) {
  if (OPTIONAL_STEPS.has(stepId)) {
    return false
  }
  if (!section) {
    return true
  }
  return section.isRequired !== false
}

function findNextVisibleStep(
  configuration: MembershipCategoryConfiguration,
  currentStepId: MembershipApplicationStepId,
  categoryId?: number | null,
) {
  const currentIndex = membershipApplicationSteps.findIndex((step) => step.id === currentStepId)
  if (currentIndex === -1) {
    return undefined
  }

  for (let index = currentIndex + 1; index < membershipApplicationSteps.length; index += 1) {
    const candidate = membershipApplicationSteps[index]
    if (!categoryId && CATEGORY_DEPENDENT_STEPS.has(candidate.id)) {
      continue
    }
    const candidateSection = configuration.sections[candidate.id]
    if (candidateSection?.isVisible !== false) {
      return candidate
    }
  }

  return undefined
}

function findPreviousVisibleStep(
  configuration: MembershipCategoryConfiguration,
  currentStepId: MembershipApplicationStepId,
  categoryId?: number | null,
) {
  const currentIndex = membershipApplicationSteps.findIndex((step) => step.id === currentStepId)
  if (currentIndex === -1) {
    return undefined
  }

  for (let index = currentIndex - 1; index >= 0; index -= 1) {
    const candidate = membershipApplicationSteps[index]
    if (!categoryId && CATEGORY_DEPENDENT_STEPS.has(candidate.id)) {
      continue
    }
    const candidateSection = configuration.sections[candidate.id]
    if (candidateSection?.isVisible !== false) {
      return candidate
    }
  }

  return undefined
}

export function useApplicationCategoryConfiguration(categoryId?: number | null) {
  const {
    categories,
    categoriesWithConfiguration,
    getConfigurationForCategoryId,
    ...queryMeta
  } = useMembershipCategoryConfigurations({
    staleTime: 5 * 60 * 1000,
  })

  const configuration = useMemo(
    () => getConfigurationForCategoryId(categoryId ?? undefined),
    [getConfigurationForCategoryId, categoryId],
  )

  const visibleSteps = useMemo(
    () => filterVisibleSteps(configuration, categoryId),
    [configuration, categoryId],
  )

  const requiredStepIds = useMemo(
    () =>
      membershipApplicationSteps
        .filter((step) => isSectionRequired(step.id, configuration.sections[step.id]))
        .map((step) => step.id),
    [configuration],
  )

  const nextStepCache = useMemo(
    () => new Map<MembershipApplicationStepId, MembershipApplicationStep | undefined>(),
    [configuration, categoryId],
  )
  const previousStepCache = useMemo(
    () => new Map<MembershipApplicationStepId, MembershipApplicationStep | undefined>(),
    [configuration, categoryId],
  )

  const getNextStep = (stepId: MembershipApplicationStepId) => {
    if (nextStepCache.has(stepId)) {
      return nextStepCache.get(stepId)
    }
    const nextStep = findNextVisibleStep(configuration, stepId, categoryId)
    nextStepCache.set(stepId, nextStep)
    return nextStep
  }

  const getPreviousStep = (stepId: MembershipApplicationStepId) => {
    if (previousStepCache.has(stepId)) {
      return previousStepCache.get(stepId)
    }
    const previousStep = findPreviousVisibleStep(configuration, stepId, categoryId)
    previousStepCache.set(stepId, previousStep)
    return previousStep
  }

  const getSection = (stepId: MembershipApplicationStepId): MembershipCategorySectionConfig | undefined => {
    return configuration.sections[stepId]
  }

  const category = useMemo(
    () => categories.find((item) => item.id === categoryId),
    [categories, categoryId],
  )

  const categoriesQuery = queryMeta

  return {
    configuration,
    category,
    categories,
    categoriesWithConfiguration,
    visibleSteps,
    requiredStepIds,
    getNextStep,
    getPreviousStep,
    getSection,
    categoriesQuery,
  }
}

export function useApplicationSectionConfiguration(
  stepId: MembershipApplicationStepId,
  categoryId?: number | null,
) {
  const {
    configuration,
    visibleSteps,
    requiredStepIds,
    getNextStep,
    getPreviousStep,
    getSection,
    category,
    categoriesQuery,
  } = useApplicationCategoryConfiguration(categoryId)

  const step = membershipApplicationSteps.find((item) => item.id === stepId)
  const section = getSection(stepId)
  const isVisible = section?.isVisible !== false
  const isRequired = isSectionRequired(stepId, section)
  const nextStep = getNextStep(stepId)
  const previousStep = getPreviousStep(stepId)

  return {
    step,
    section,
    configuration,
    category,
    visibleSteps,
    requiredStepIds,
    nextStep,
    previousStep,
    isVisible,
    isRequired,
    categoriesQuery,
  }
}
